import { API_CONFIG } from '../config/apiConfig';
import { getAuthToken } from '../utils/firebase';
import type {
  SessionListResponse,
  CreateSessionResponse,
  SessionDetail,
  JoinSessionResponse,
  SessionStatus,
  NextGameResponse,
  RoundReadyResponse,
  SessionGamesResponse,
  FullGameStateForUser,
  BoardLayout,
  PositioningData,
  ValidPosition,
  User,
  HomeData,
  ProfileData,
  BotSessionResponse,
  AddBotResponse,
  LobbyState,
  StartGameResponse,
  ApiError,
  StatsData,
  MatchHistoryResponse,
  PartnerStatsResponse,
  AdminPresenceResponse,
  MatchTypeFilter,
  LeaderboardResponse,
  UpdateCountryResult,
  UpdateLocaleResult,
  UpdateAvatarResult,
  RematchSnapshotWire,
} from '../types/game';
import type { AchievementsResponse } from '../types/achievements';
import type {
  FriendsListResponse,
  FriendRequestsResponse,
  FriendshipResponse,
  UserSearchResponse,
  InvitationsListResponse,
  InvitationResponse,
  ConversationsResponse,
  MessageThreadResponse,
  MarkReadResponse,
  HideConversationResponse,
  SendMessageResponse,
} from '../types/social';

interface PlayTileResponse {
  message: string;
  game_state: FullGameStateForUser;
  board_layout: BoardLayout;
  positioning_data: PositioningData;
}

interface PassTurnResponse {
  message: string;
  game_state: FullGameStateForUser;
}

interface BoardResponse {
  board_layout: BoardLayout;
  positioning_data: PositioningData;
  game_info: {
    id: number;
    status: string;
    current_turn: number;
    current_turn_user_id: number;
    board_chain_length: number;
  };
}

interface ValidPositionsResponse {
  tile_id: number;
  tile_values: { value_a: number; value_b: number };
  valid_positions: ValidPosition[];
}

/**
 * Result of PATCH /me/username. Discriminated so the caller can react to
 * each server outcome without parsing raw errors. Settings V1 extended the
 * backend contract from set-once to set-AND-rename; `username_already_set`
 * is kept for deploy skew (an older backend can still return it — the UI
 * shows the carried authoritative `username` instead of implying the typed
 * name was saved). `unknown` covers 5xx/unexpected shapes and is retryable.
 */
export type UpdateUsernameResult =
  | { ok: true;  username: string }
  | { ok: false; error: 'username_taken' | 'invalid_username'; message: string; code?: string }
  | { ok: false; error: 'username_already_set'; username: string; message: string }
  | { ok: false; error: 'unknown'; message: string; code?: string };

/** Result of PATCH /me/table_skin (Settings V1 account default). */
export type UpdateTableSkinResult =
  | { ok: true;  preferred_table_skin: string | null }
  | { ok: false; error: 'invalid_table_skin' | 'unknown'; message: string; code?: string };

/** Result of PATCH /me/tile_skin (Settings V1 account default). */
export type UpdateTileSkinResult =
  | { ok: true;  preferred_tile_skin: string | null }
  | { ok: false; error: 'invalid_tile_skin' | 'unknown'; message: string; code?: string };

export type UpdateHdrResult =
  | { ok: true;  hdr_enabled: boolean }
  | { ok: false; error: 'unknown'; message: string };

/**
 * Invalidate the global active-game surface (ActiveGameContext) after a mutation
 * that creates/advances a game. Plain window-event bus — this app has no query
 * cache to invalidate. Leaving/forfeiting dispatches this from useLeaveGameGuard.
 */
function notifyActiveGameChanged(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('active-game-changed'));
  }
}

/**
 * Localization Phase 12 (Slice C1): the error ApiClient throws for a non-OK
 * response. `message` stays the backend's legacy `error` prose EXACTLY as
 * before (every existing `e.message` consumer is unchanged); `code`/`params`
 * are the additive stable contract new consumers map to localized catalogs,
 * and `status` rides along for recovery decisions. Never translated here —
 * translation happens only at the render boundary (see i18n/uiMessage.ts).
 */
export class ApiRequestError extends Error {
  readonly code?: string;
  readonly params?: Record<string, string | number>;
  readonly status: number;

  constructor(message: string, opts: { code?: string; params?: Record<string, string | number>; status: number }) {
    super(message);
    this.name = 'ApiRequestError';
    this.code = opts.code;
    this.params = opts.params;
    this.status = opts.status;
  }
}

/**
 * Phase 12 (Slice B3b/C3b): the /me/* endpoints publish the ORIGINAL dual
 * shape `{ error: <machine id>, message: <prose> }` and the client
 * discriminates on `error`; the backend now ALSO sends an additive `code`.
 * Read it verbatim — never translated here, never derived from prose.
 */
function codeOf(body: Record<string, unknown>): string | undefined {
  return typeof body.code === 'string' ? body.code : undefined;
}

class ApiClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_CONFIG.baseUrl;
  }

  // ─── Internal Helpers ────────────────────────────────────

  private async getHeaders(): Promise<HeadersInit> {
    const token = await getAuthToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }

  private async request<T>(
    method: string,
    endpoint: string,
    body?: Record<string, unknown>
  ): Promise<T> {
    const headers = await this.getHeaders();
    const url = `${this.baseUrl}${endpoint}`;
    console.log('[API]', method, url);

    const options: RequestInit = {
      method,
      headers,
      cache: 'no-store',
    };

    if (body && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
      options.body = JSON.stringify(body);
    }

    const response = await fetch(url, options);

    if (!response.ok) {
      const errorData = (await response.json().catch(() => ({
        error: `HTTP ${response.status}: ${response.statusText}`,
      }))) as ApiError & { code?: string; params?: Record<string, string | number> };
      throw new ApiRequestError(errorData.error || `HTTP ${response.status}`, {
        code:   typeof errorData.code === 'string' ? errorData.code : undefined,
        params: errorData.params,
        status: response.status,
      });
    }

    return response.json() as Promise<T>;
  }

  private get<T>(endpoint: string): Promise<T> {
    return this.request<T>('GET', endpoint);
  }

  private post<T>(endpoint: string, body?: Record<string, unknown>): Promise<T> {
    return this.request<T>('POST', endpoint, body);
  }

  private patch<T>(endpoint: string, body?: Record<string, unknown>): Promise<T> {
    return this.request<T>('PATCH', endpoint, body);
  }

  private delete<T>(endpoint: string): Promise<T> {
    return this.request<T>('DELETE', endpoint);
  }

  // ─── Sessions ────────────────────────────────────────────

  /** GET /api/v1/sessions — list sessions + my active session/game */
  async getSessions(): Promise<SessionListResponse> {
    return this.get<SessionListResponse>(API_CONFIG.endpoints.sessions);
  }

  /** POST /api/v1/sessions — create an empty session */
  async createSession(): Promise<CreateSessionResponse> {
    return this.post<CreateSessionResponse>(API_CONFIG.endpoints.sessions);
  }

  /** POST /api/v1/sessions/bot — create a bot session and start immediately */
  async createBotSession(difficulty: 'easy' | 'medium' | 'hard' = 'medium'): Promise<BotSessionResponse> {
    const res = await this.post<BotSessionResponse>(API_CONFIG.endpoints.botSession, { difficulty });
    notifyActiveGameChanged();
    return res;
  }

  /** GET /api/v1/sessions/:id — full session details with players */
  async getSession(id: number): Promise<SessionDetail> {
    return this.get<SessionDetail>(API_CONFIG.endpoints.session(id));
  }

  /** POST /api/v1/sessions/:id/join — join a session */
  async joinSession(
    id: number,
    preferredTeam?: number,
    preferredPosition?: number
  ): Promise<JoinSessionResponse> {
    return this.post<JoinSessionResponse>(API_CONFIG.endpoints.joinSession(id), {
      preferred_team: preferredTeam,
      preferred_position: preferredPosition,
    });
  }

  /** POST /api/v1/sessions/:id/leave — leave a session (forfeit for active games) */
  async leaveSession(id: number): Promise<{ message: string; session_id: number; status: string }> {
    return this.post(API_CONFIG.endpoints.leaveSession(id));
  }

  /** POST /api/v1/sessions/:id/rematch_vote — final-match rematch vote.
   *  Returns the authoritative rematch snapshot; navigation follows the
   *  authoritative result (response or rematch_state broadcast), never local
   *  button state. */
  async submitRematchVote(
    id: number,
    vote: 'yes' | 'no'
  ): Promise<{ message: string; status: string; session_id: number; rematch: RematchSnapshotWire | null }> {
    return this.post(API_CONFIG.endpoints.rematchVote(id), { vote });
  }

  /**
   * POST /api/v1/sessions/:id/close_solo — permanently close a SOLO bot game.
   * NOT a forfeit (no abandonment/Elo). Rejected by the backend for multiplayer.
   */
  async closeSoloGame(id: number): Promise<{ message: string; session_id: number; status: string }> {
    const res = await this.post<{ message: string; session_id: number; status: string }>(
      API_CONFIG.endpoints.closeSoloSession(id),
    );
    notifyActiveGameChanged();
    return res;
  }

  /** GET /api/v1/sessions/:id/status — session status */
  async getSessionStatus(id: number): Promise<SessionStatus> {
    return this.get<SessionStatus>(API_CONFIG.endpoints.sessionStatus(id));
  }

  /** POST /api/v1/sessions/:id/next_game — start next game */
  async nextGame(id: number): Promise<NextGameResponse> {
    const res = await this.post<NextGameResponse>(API_CONFIG.endpoints.nextGame(id));
    notifyActiveGameChanged();
    return res;
  }

  /** POST /api/v1/sessions/:id/round_ready — mark THIS player ready for the next
   *  round. The server decides when to actually advance (all humans ready, or
   *  the 15 s deadline). This never creates a round just because one player
   *  pressed it — the transition arrives via the next_game_created broadcast. */
  async markRoundReady(id: number): Promise<RoundReadyResponse> {
    return this.post<RoundReadyResponse>(API_CONFIG.endpoints.roundReady(id));
  }

  /** GET /api/v1/sessions/:id/games — the session's per-round history
   *  (participant-only). Read-only; Match End derives the rounds-won count
   *  from each finished game's authoritative winner_team. */
  async getSessionGames(id: number): Promise<SessionGamesResponse> {
    return this.get<SessionGamesResponse>(API_CONFIG.endpoints.sessionGames(id));
  }

  /** POST /api/v1/sessions/:id/add_bot — add a bot to one empty seat */
  async addBot(
    sessionId: number,
    position: number,
    difficulty: 'easy' | 'medium' | 'hard'
  ): Promise<AddBotResponse> {
    return this.post<AddBotResponse>(API_CONFIG.endpoints.addBot(sessionId), {
      position,
      difficulty,
    });
  }

  /** POST /api/v1/sessions/:id/fill_bots — fill all empty seats with bots */
  async fillBots(
    sessionId: number,
    difficulty: 'easy' | 'medium' | 'hard'
  ): Promise<AddBotResponse> {
    return this.post<AddBotResponse>(API_CONFIG.endpoints.fillBots(sessionId), { difficulty });
  }

  // ─── Lobby v1: host-authoritative start + lobby controls ─────────────────

  /** POST /api/v1/sessions/:id/start — HOST ONLY. Idempotent authoritative start. */
  async startGame(sessionId: number): Promise<StartGameResponse> {
    const res = await this.post<StartGameResponse>(API_CONFIG.endpoints.startGame(sessionId));
    notifyActiveGameChanged();
    return res;
  }

  /** PATCH /api/v1/sessions/:id/host_theme — HOST ONLY. Resets guest readiness. */
  async setHostTheme(
    sessionId: number,
    theme: { host_table_skin?: string; host_tile_skin?: string }
  ): Promise<LobbyState> {
    return this.patch<LobbyState>(API_CONFIG.endpoints.hostTheme(sessionId), theme);
  }

  /** PATCH /api/v1/sessions/:id/visibility — HOST ONLY. */
  async setVisibility(sessionId: number, visibility: 'public' | 'private'): Promise<LobbyState> {
    return this.patch<LobbyState>(API_CONFIG.endpoints.sessionVisibility(sessionId), { visibility });
  }

  /** PATCH /api/v1/sessions/:id/appearance — participant (self). Auto-unreadies. */
  async setAppearance(
    sessionId: number,
    appearance: { appearance_mode?: 'host' | 'personal'; selected_table_skin?: string; selected_tile_skin?: string }
  ): Promise<LobbyState> {
    return this.patch<LobbyState>(API_CONFIG.endpoints.appearance(sessionId), appearance);
  }

  /** POST /api/v1/sessions/:id/ready — participant (self) marks Ready. */
  async setReady(sessionId: number): Promise<LobbyState> {
    return this.post<LobbyState>(API_CONFIG.endpoints.ready(sessionId));
  }

  /** DELETE /api/v1/sessions/:id/ready — participant (self) clears Ready. */
  async clearReady(sessionId: number): Promise<LobbyState> {
    return this.delete<LobbyState>(API_CONFIG.endpoints.ready(sessionId));
  }

  /** Host-only: remove a seated bot or human guest while the lobby is waiting.
   *  Identifies the target by user_id (already public in the lobby payload —
   *  never an email). Returns the refreshed authoritative lobby state. */
  async removeSeat(sessionId: number, userId: number): Promise<LobbyState> {
    return this.delete<LobbyState>(API_CONFIG.endpoints.removeSeat(sessionId, userId));
  }

  /** Participant self-service: take an empty seat while waiting (lobby
   *  equivalent of clicking an open seat in Play Online). Returns the refreshed
   *  authoritative lobby state. */
  async moveSeat(sessionId: number, position: number): Promise<LobbyState> {
    return this.post<LobbyState>(API_CONFIG.endpoints.moveSeat(sessionId), { position });
  }

  // ─── Games ───────────────────────────────────────────────

  /** GET /api/v1/games/:id/state — full game state for current user */
  async getGameState(id: number): Promise<FullGameStateForUser> {
    return this.get<FullGameStateForUser>(API_CONFIG.endpoints.gameState(id));
  }

  /** GET /api/v1/games/:id/spectate — read-only, hand-blind state for a
   *  friend spectating an in-progress multiplayer game. Same wire shape as
   *  getGameState (FullGameStateForUser) so the exact same rendering path
   *  (GameScene) can consume it unchanged — my_hand is always [],
   *  my_position/my_team are always -1 (see Game#game_state_for_spectator). */
  async getSpectatorGameState(id: number): Promise<FullGameStateForUser> {
    return this.get<FullGameStateForUser>(API_CONFIG.endpoints.spectateGame(id));
  }

  /** POST /api/v1/games/:id/play — play a tile */
  async playTile(
    gameId: number,
    tileId: number,
    position: 'left' | 'right'
  ): Promise<PlayTileResponse> {
    return this.post<PlayTileResponse>(API_CONFIG.endpoints.playTile(gameId), {
      tile_id: tileId,
      position,
    });
  }

  /** POST /api/v1/games/:id/pass — pass turn */
  async passTurn(gameId: number): Promise<PassTurnResponse> {
    return this.post<PassTurnResponse>(API_CONFIG.endpoints.passTurn(gameId));
  }

  /** GET /api/v1/games/:id/board — board layout + positioning data */
  async getBoard(gameId: number): Promise<BoardResponse> {
    return this.get<BoardResponse>(API_CONFIG.endpoints.board(gameId));
  }

  /** GET /api/v1/games/:id/board/valid_positions/:tile_id */
  async getValidPositions(gameId: number, tileId: number): Promise<ValidPositionsResponse> {
    return this.get<ValidPositionsResponse>(
      API_CONFIG.endpoints.validPositions(gameId, tileId)
    );
  }

  // ─── User ────────────────────────────────────────────────

  /** GET /api/v1/me — current user info, including registration_complete */
  async getMe(): Promise<User> {
    return this.get<User>(API_CONFIG.endpoints.me);
  }

  /**
   * DELETE /api/v1/me — Account Deletion (App Store Guideline 5.1.1(v) /
   * Google Play Account Deletion policy). Anonymizes the account server-side
   * (live sessions are left/forfeited, friendships/invitations/achievements/
   * push subscriptions are removed, PII columns are cleared) and best-effort
   * deletes the underlying Firebase Auth credential.
   *
   * This does NOT sign the caller out — it only makes the account
   * unusable server-side. The caller must call Firebase `signOut()`
   * immediately after this resolves (see useDeleteAccount / AuthContext);
   * otherwise the current in-memory session would still look "logged in"
   * on the client until the next token refresh gets rejected.
   */
  async deleteAccount(): Promise<{ message: string }> {
    return this.delete<{ message: string }>(API_CONFIG.endpoints.me);
  }

  /**
   * PATCH /api/v1/me/username — Step 2 of email registration (also the
   * resume path for an incomplete-registration user).
   *
   * Returns a discriminated result rather than throwing on a known
   * server-side outcome, because 'username_already_set' carries the
   * caller's authoritative already-stored `username` that the UI must show
   * (see SignupUsernameScreen). Only a genuine transport failure (fetch
   * rejects) throws — the caller treats that as retryable. This is
   * deliberately its own fetch (not the shared `request<T>`, which discards
   * the error body) so no other endpoint's error contract changes.
   */
  async updateUsername(username: string): Promise<UpdateUsernameResult> {
    const headers = await this.getHeaders();
    const res = await fetch(`${this.baseUrl}${API_CONFIG.endpoints.updateUsername}`, {
      method: 'PATCH',
      headers,
      cache:  'no-store',
      body:   JSON.stringify({ username }),
    });
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;

    if (res.ok) {
      return { ok: true, username: String(body.username ?? '') };
    }
    const error = body.error as string | undefined;
    if (error === 'username_already_set') {
      return { ok: false, error, username: String(body.username ?? ''), message: String(body.message ?? '') };
    }
    if (error === 'username_taken' || error === 'invalid_username') {
      return { ok: false, error, message: String(body.message ?? ''), code: codeOf(body) };
    }
    // 5xx / unexpected shape — retryable, not a distinct user-facing case.
    return { ok: false, error: 'unknown', message: String(body.message ?? `HTTP ${res.status}`) };
  }

  /**
   * PATCH /api/v1/me/country — set or clear the Country/Region selection
   * (Country Flag System V1). Pass null to clear. Mirrors updateUsername's
   * discriminated-result pattern (own fetch so the 422 error body survives);
   * only a transport failure throws.
   */
  async updateCountry(countryCode: string | null): Promise<UpdateCountryResult> {
    const headers = await this.getHeaders();
    const res = await fetch(`${this.baseUrl}${API_CONFIG.endpoints.updateCountry}`, {
      method: 'PATCH',
      headers,
      cache:  'no-store',
      body:   JSON.stringify({ country_code: countryCode }),
    });
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;

    if (res.ok) {
      return { ok: true, country_code: (body.country_code as string | null) ?? null };
    }
    if (body.error === 'invalid_country_code') {
      return { ok: false, error: 'invalid_country_code', message: String(body.message ?? ''), code: codeOf(body) };
    }
    return { ok: false, error: 'unknown', message: String(body.message ?? `HTTP ${res.status}`) };
  }

  /**
   * PATCH /api/v1/me/avatar — mutable preset-avatar selection (Player
   * Identity V1). Self-only server-side; null clears the selection. Same
   * discriminated-result pattern as updateCountry (own fetch so the 422
   * error body survives); only a transport failure throws.
   */
  async updateAvatar(avatarKey: string | null): Promise<UpdateAvatarResult> {
    const headers = await this.getHeaders();
    const res = await fetch(`${this.baseUrl}${API_CONFIG.endpoints.updateAvatar}`, {
      method: 'PATCH',
      headers,
      cache:  'no-store',
      body:   JSON.stringify({ avatar_key: avatarKey }),
    });
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;

    if (res.ok) {
      return { ok: true, avatar_key: (body.avatar_key as string | null) ?? null };
    }
    if (body.error === 'invalid_avatar_key') {
      return { ok: false, error: 'invalid_avatar_key', message: String(body.message ?? ''), code: codeOf(body) };
    }
    return { ok: false, error: 'unknown', message: String(body.message ?? `HTTP ${res.status}`) };
  }

  /**
   * PATCH /api/v1/me/table_skin — account-level preferred table skin
   * (Settings V1). A DEFAULT ONLY: seeds future lobby/session cosmetics at
   * creation/join time server-side and never mutates an existing session.
   * Null clears the preference. Same discriminated-result pattern as
   * updateCountry; only a transport failure throws.
   */
  async updateTableSkin(tableSkin: string | null): Promise<UpdateTableSkinResult> {
    const headers = await this.getHeaders();
    const res = await fetch(`${this.baseUrl}${API_CONFIG.endpoints.updateTableSkin}`, {
      method: 'PATCH',
      headers,
      cache:  'no-store',
      body:   JSON.stringify({ table_skin: tableSkin }),
    });
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;

    if (res.ok) {
      return { ok: true, preferred_table_skin: (body.preferred_table_skin as string | null) ?? null };
    }
    if (body.error === 'invalid_table_skin') {
      return { ok: false, error: 'invalid_table_skin', message: String(body.message ?? ''), code: codeOf(body) };
    }
    return { ok: false, error: 'unknown', message: String(body.message ?? `HTTP ${res.status}`) };
  }

  /**
   * PATCH /api/v1/me/tile_skin — account-level preferred tile skin
   * (Settings V1). Same contract as updateTableSkin.
   */
  async updateTileSkin(tileSkin: string | null): Promise<UpdateTileSkinResult> {
    const headers = await this.getHeaders();
    const res = await fetch(`${this.baseUrl}${API_CONFIG.endpoints.updateTileSkin}`, {
      method: 'PATCH',
      headers,
      cache:  'no-store',
      body:   JSON.stringify({ tile_skin: tileSkin }),
    });
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;

    if (res.ok) {
      return { ok: true, preferred_tile_skin: (body.preferred_tile_skin as string | null) ?? null };
    }
    if (body.error === 'invalid_tile_skin') {
      return { ok: false, error: 'invalid_tile_skin', message: String(body.message ?? ''), code: codeOf(body) };
    }
    return { ok: false, error: 'unknown', message: String(body.message ?? `HTTP ${res.status}`) };
  }

  /**
   * PATCH /api/v1/me/locale — persist the account-level language preference
   * (Localization Phase 10). Only exact supported values ('en' | 'es-419');
   * the backend rejects everything else with the stable 'invalid_locale'
   * code. Mirrors updateCountry's discriminated-result pattern (own fetch so
   * the 422 error body survives); only a transport failure throws.
   */
  async updateLocale(locale: string): Promise<UpdateLocaleResult> {
    const headers = await this.getHeaders();
    const res = await fetch(`${this.baseUrl}${API_CONFIG.endpoints.updateLocale}`, {
      method: 'PATCH',
      headers,
      cache:  'no-store',
      body:   JSON.stringify({ preferred_locale: locale }),
    });
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;

    if (res.ok) {
      return { ok: true, preferred_locale: (body.preferred_locale as string | null) ?? null };
    }
    if (body.error === 'invalid_locale') {
      return { ok: false, error: 'invalid_locale', message: String(body.message ?? ''), code: codeOf(body) };
    }
    return { ok: false, error: 'unknown', message: String(body.message ?? `HTTP ${res.status}`) };
  }

  async updateHdr(enabled: boolean): Promise<UpdateHdrResult> {
    const headers = await this.getHeaders();
    const res = await fetch(`${this.baseUrl}${API_CONFIG.endpoints.updateHdr}`, {
      method: 'PATCH',
      headers,
      cache:  'no-store',
      body:   JSON.stringify({ hdr_enabled: enabled }),
    });
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;

    if (res.ok) {
      return { ok: true, hdr_enabled: Boolean(body.hdr_enabled) };
    }
    return { ok: false, error: 'unknown', message: String(body.message ?? `HTTP ${res.status}`) };
  }

  // Push mutations (preference PATCH, subscription POST and DELETE)
  // deliberately have NO ApiClient methods: the backend upserts subscription
  // ownership by token and every push write is account-scoped, so all of
  // them must go through the identity-guarded seam in
  // utils/pushSubscriptionWriter.ts (a structural test enforces this).

  /** GET /api/v1/users/username_available — UX-only pre-check, not authoritative. */
  async checkUsernameAvailable(username: string): Promise<{ available: boolean }> {
    return this.get(API_CONFIG.endpoints.usernameAvailable(username));
  }

  /** GET /api/v1/home — home screen data (identity + active game + last match) */
  async getHome(): Promise<HomeData> {
    return this.get<HomeData>(API_CONFIG.endpoints.home);
  }

  /** GET /api/v1/profile — profile screen data (identity + stats) */
  async getProfile(): Promise<ProfileData> {
    return this.get<ProfileData>(API_CONFIG.endpoints.profile);
  }

  async getAchievements(): Promise<AchievementsResponse> {
    return this.get<AchievementsResponse>(API_CONFIG.endpoints.achievements);
  }

  // ─── Stats V1 ────────────────────────────────────────────────

  /** GET /api/v1/stats/me — lifetime stats. Omit matchType for the backend default (online_ranked). */
  async getStats(matchType?: MatchTypeFilter): Promise<StatsData> {
    return this.get<StatsData>(API_CONFIG.endpoints.statsMe(matchType));
  }

  /** GET /api/v1/stats/history — match history (newest first). Omit matchType for the backend default (online_ranked). */
  async getMatchHistory(matchType?: MatchTypeFilter): Promise<MatchHistoryResponse> {
    return this.get<MatchHistoryResponse>(API_CONFIG.endpoints.statsHistory(matchType));
  }

  /** GET /api/v1/stats/partners — per-partner aggregated stats. Omit matchType for the backend default (online_ranked). */
  async getPartnerStats(matchType?: MatchTypeFilter): Promise<PartnerStatsResponse> {
    return this.get<PartnerStatsResponse>(API_CONFIG.endpoints.statsPartners(matchType));
  }

  // ─── Leaderboard V1 ──────────────────────────────────────────

  /** GET /api/v1/leaderboard — ranked-only global leaderboard, page 1 by default (no pagination UI yet). */
  async getLeaderboard(page = 1, perPage = 50): Promise<LeaderboardResponse> {
    return this.get<LeaderboardResponse>(API_CONFIG.endpoints.leaderboard(page, perPage));
  }

  // ─── Admin ───────────────────────────────────────────────────

  /** GET /api/v1/admin/presence — read-only user presence dashboard (admin only) */
  async getAdminPresence(): Promise<AdminPresenceResponse> {
    return this.get<AdminPresenceResponse>(API_CONFIG.endpoints.adminPresence);
  }

  // ─── Friends ─────────────────────────────────────────────────

  /** GET /api/v1/friends — accepted friends with online status */
  async listFriends(): Promise<FriendsListResponse> {
    return this.get<FriendsListResponse>(API_CONFIG.endpoints.friends);
  }

  /** GET /api/v1/friends/pending — incoming pending friend requests */
  async listPendingRequests(): Promise<FriendRequestsResponse> {
    return this.get<FriendRequestsResponse>(API_CONFIG.endpoints.friendsPending);
  }

  /** GET /api/v1/users/search?q=prefix — email-prefix search with friendship status */
  async searchUsers(q: string): Promise<UserSearchResponse> {
    const encoded = encodeURIComponent(q);
    return this.get<UserSearchResponse>(`${API_CONFIG.endpoints.usersSearch}?q=${encoded}`);
  }

  /** POST /api/v1/friends — send a friend request by email */
  async sendFriendRequest(email: string): Promise<FriendshipResponse> {
    return this.post<FriendshipResponse>(API_CONFIG.endpoints.friends, { email });
  }

  /**
   * POST /api/v1/friends/from_game — send a friend request to a player named
   * via a shared game context (in-game/spectator "Add Friend"), instead of
   * by email. See Api::V1::Friends::GameContextAuthorization on the backend.
   */
  async sendFriendRequestFromGame(userId: number, gameId: number): Promise<FriendshipResponse> {
    return this.post<FriendshipResponse>(API_CONFIG.endpoints.friendsFromGame, { user_id: userId, game_id: gameId });
  }

  /** POST /api/v1/friends/:id/accept — accept an incoming request */
  async acceptFriendRequest(friendshipId: number): Promise<FriendshipResponse> {
    return this.post<FriendshipResponse>(API_CONFIG.endpoints.acceptFriend(friendshipId));
  }

  /** POST /api/v1/friends/:id/decline — decline an incoming request */
  async declineFriendRequest(friendshipId: number): Promise<{ message: string }> {
    return this.post<{ message: string }>(API_CONFIG.endpoints.declineFriend(friendshipId));
  }

  /** DELETE /api/v1/friends/:id — cancel a pending request or remove an accepted friend */
  async removeFriend(friendshipId: number): Promise<{ message: string }> {
    return this.delete<{ message: string }>(API_CONFIG.endpoints.friend(friendshipId));
  }

  // ─── Game Invitations ─────────────────────────────────────────

  /** GET /api/v1/game_invitations — pending incoming and outgoing invitations */
  async listInvitations(): Promise<InvitationsListResponse> {
    return this.get<InvitationsListResponse>(API_CONFIG.endpoints.gameInvitations);
  }

  /** POST /api/v1/game_invitations — send a game invitation */
  async sendInvitation(toUserId: number, sessionId: number): Promise<InvitationResponse> {
    return this.post<InvitationResponse>(API_CONFIG.endpoints.gameInvitations, {
      to_user_id: toUserId,
      session_id: sessionId,
    });
  }

  /** POST /api/v1/game_invitations/:id/accept — accept an invitation */
  async acceptInvitation(invitationId: number): Promise<InvitationResponse> {
    return this.post<InvitationResponse>(API_CONFIG.endpoints.acceptGameInvitation(invitationId));
  }

  /** POST /api/v1/game_invitations/:id/decline — decline an invitation */
  async declineInvitation(invitationId: number): Promise<InvitationResponse> {
    return this.post<InvitationResponse>(API_CONFIG.endpoints.declineGameInvitation(invitationId));
  }

  /** DELETE /api/v1/game_invitations/:id — cancel a pending outgoing invitation */
  async cancelInvitation(invitationId: number): Promise<{ message: string }> {
    return this.delete<{ message: string }>(API_CONFIG.endpoints.gameInvitation(invitationId));
  }

  // ─── Direct Messages ────────────────────────────────────────

  async getConversations(): Promise<ConversationsResponse> {
    return this.get<ConversationsResponse>(API_CONFIG.endpoints.conversations);
  }

  /**
   * DM-dock foundation: history is the NEWEST page (chronological within the
   * page). With no options the request URL is byte-identical to the legacy
   * call, so the server's legacy mark-read side effect still applies —
   * existing screens keep their exact behavior. The dock's data layer passes
   * `markRead: false` (fetch without acknowledging) and `beforeId` (the page
   * strictly before that message id) instead.
   */
  async getMessageThread(
    userId: number,
    opts?: { beforeId?: number; markRead?: boolean },
  ): Promise<MessageThreadResponse> {
    const params = new URLSearchParams();
    if (opts?.beforeId != null)       params.set('before_id', String(opts.beforeId));
    if (opts?.markRead === false)     params.set('mark_read', 'false');
    const query = params.toString();
    const endpoint = query
      ? `${API_CONFIG.endpoints.messageThread(userId)}?${query}`
      : API_CONFIG.endpoints.messageThread(userId);
    return this.get<MessageThreadResponse>(endpoint);
  }

  /** DM-dock foundation: explicit, idempotent read acknowledgement for one
   *  counterpart's incoming messages — decoupled from fetching history. */
  async markConversationRead(userId: number): Promise<MarkReadResponse> {
    return this.patch<MarkReadResponse>(`${API_CONFIG.endpoints.messageThread(userId)}/read`);
  }

  /**
   * "Remove from my list": deletes only the caller's conversation-LIST entry
   * for this counterpart — no message is deleted, the counterpart's list is
   * unaffected, and any newer message makes the conversation reappear with
   * its full history. Idempotent.
   */
  async hideConversation(userId: number): Promise<HideConversationResponse> {
    return this.delete<HideConversationResponse>(`${API_CONFIG.endpoints.conversations}/${userId}`);
  }

  async sendMessage(recipientId: number, body: string, image?: File): Promise<SendMessageResponse> {
    if (!image) {
      return this.post<SendMessageResponse>(API_CONFIG.endpoints.directMessages, {
        recipient_id: recipientId,
        body,
      });
    }

    const token = await getAuthToken();
    const headers: HeadersInit = { Accept: 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const form = new FormData();
    form.append('recipient_id', String(recipientId));
    form.append('body', body);
    form.append('image', image);

    const res = await fetch(`${this.baseUrl}${API_CONFIG.endpoints.directMessages}`, {
      method: 'POST',
      headers,
      cache:  'no-store',
      body:   form,
    });

    if (!res.ok) {
      const errorData = (await res.json().catch(() => ({
        error: `HTTP ${res.status}: ${res.statusText}`,
      }))) as ApiError & { code?: string; params?: Record<string, string | number> };
      // Same additive contract as the shared request<T> seam (Phase 12 B3a):
      // the message is the byte-identical legacy prose; code/params/status
      // ride along so image-send failures can localize like text sends.
      throw new ApiRequestError(errorData.error || `HTTP ${res.status}`, {
        code:   typeof errorData.code === 'string' ? errorData.code : undefined,
        params: errorData.params,
        status: res.status,
      });
    }

    return res.json() as Promise<SendMessageResponse>;
  }
}

/** Singleton API client instance */
export const apiClient = new ApiClient();
